"use client";

import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { FiEdit2, FiPlus, FiTrash2, FiX } from "react-icons/fi";
import PatientReportedMedicationInput from "@/components/patient-reported-medication-input";
import { Button, Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, Input } from "@/components/ui";
import createId from "@/lib/create-id";
import { parseError } from "@/lib/parse-error";
import { getHealthFieldId, getHealthFieldValueDetails, getHealthFieldValues } from "@/lib/patient-health-fields";
import { useToast } from "@/lib/use-toast";
import { PatientDynamicFieldType } from "@/types/enums/patient-health-field";
import type { DynamicHealthFieldValue } from "@/types/patient";
import { useUpdatePatientDynamicFieldsMutation } from "@/api/provider/user/patient/patient-mutation";
import { patientQueryOptions } from "@/api/provider/user/patient/patient-query";
interface PatientHealthFieldDialogConfig {
  type: PatientDynamicFieldType;
  title: string;
  description: string;
  placeholder: string;
  addButtonText: string;
  addAnotherButtonText: string;
}
interface PatientHealthFieldDialogProps {
  children?: React.ReactNode;
  config: PatientHealthFieldDialogConfig;
  patientId: string;
}
export default function PatientHealthFieldDialog({
  children,
  config,
  patientId
}: PatientHealthFieldDialogProps) {
  const {
    data: patient
  } = useQuery({
    ...patientQueryOptions.detail(patientId),
    enabled: !!patientId
  });
  const {
    toast
  } = useToast();
  const [open, setOpen] = useState(false);
  const [itemList, setItemList] = useState<string[]>([]);
  const [medicationEntries, setMedicationEntries] = useState<DynamicHealthFieldValue[]>([]);
  const [inputFields, setInputFields] = useState<{
    id: string;
    value: string;
  }[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState<string>("");
  const updateDynamicFieldsMutation = useUpdatePatientDynamicFieldsMutation(patientId);
  const useMedicationSuggestions = config.type === PatientDynamicFieldType.MEDICATIONS || config.type === PatientDynamicFieldType.PRESCRIPTIONS;
  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (isOpen && patient) {
      setItemList(getHealthFieldValues(patient, config.type));
      setMedicationEntries(getHealthFieldValueDetails(patient, config.type));
      setInputFields([{
        id: createId("med"),
        value: ""
      }]);
      setEditingIndex(null);
      setEditingValue("");
    }
  };
  const addNewInputField = () => {
    setInputFields([...inputFields, {
      id: createId("med"),
      value: ""
    }]);
  };
  const updateInputField = (id: string, value: string) => {
    setInputFields(fields => fields.map(field => field.id === id ? {
      ...field,
      value
    } : field));
  };
  const removeInputField = (id_0: string) => {
    setInputFields(fields_0 => fields_0.filter(field_0 => field_0.id !== id_0));
  };
  const addItemFromInput = (id_1: string, value_0: string) => {
    const trimmedValue = value_0.trim();
    if (!trimmedValue) return;
    // Functional update so a concurrent blur-save/remove doesn't clobber this
    setItemList(prev => prev.includes(trimmedValue) ? prev : [...prev, trimmedValue]);
    // Clear the input but keep it
    updateInputField(id_1, "");
  };
  const handleRemoveItem = (index: number) => {
    setItemList(prev_0 => prev_0.filter((_, i) => i !== index));
    // Discard any pending inline edit; removing a row invalidates editingIndex
    setEditingIndex(null);
    setEditingValue("");
  };
  const handleEditItem = (index_0: number) => {
    setEditingIndex(index_0);
    setEditingValue(itemList[index_0]);
  };
  const handleSaveEdit = (index_1: number) => {
    const trimmedValue_0 = editingValue.trim();
    if (trimmedValue_0) {
      // Functional update so a concurrent click (e.g. deleting another row)
      // merges with this save instead of overwriting it from a stale snapshot
      setItemList(prev_1 => {
        const otherItems = prev_1.filter((__0, i_0) => i_0 !== index_1);
        if (otherItems.includes(trimmedValue_0)) return prev_1;
        const updated = [...prev_1];
        updated[index_1] = trimmedValue_0;
        return updated;
      });
    }
    setEditingIndex(null);
    setEditingValue("");
  };
  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditingValue("");
  };
  const handleSave = async () => {
    if (!patient) return;

    // Collect all non-empty input values and add them to item list
    const finalItemList = [...itemList];
    // Commit any in-progress inline edit (footer Save clicked while editing a row)
    if (editingIndex !== null) {
      const trimmedEdit = editingValue.trim();
      if (trimmedEdit && !finalItemList.some((item, i_1) => i_1 !== editingIndex && item === trimmedEdit)) {
        finalItemList[editingIndex] = trimmedEdit;
      }
    }
    if (!useMedicationSuggestions) {
      inputFields.forEach(field_1 => {
        if (field_1.value.trim() && !finalItemList.includes(field_1.value.trim())) {
          finalItemList.push(field_1.value.trim());
        }
      });
    }
    setIsSaving(true);
    try {
      // Use the utility function to get the field ID
      const fieldId = getHealthFieldId(patient, config.type);
      if (!fieldId) {
        toast({
          title: "Error",
          description: `${config.title} field not found. Please try again.`,
          variant: "error"
        });
        setIsSaving(false);
        return;
      }

      // Update backend using field ID
      const values = useMedicationSuggestions ? medicationEntries : finalItemList;
      const result = await updateDynamicFieldsMutation.mutateAsync({
        [fieldId]: values
      });
      if (result) {
        toast({
          title: "Success",
          description: `${config.title} updated successfully`,
          variant: "success"
        });
        setOpen(false);
      } else {
        toast({
          title: "Error",
          description: `Failed to update ${config.title.toLowerCase()}. Please try again.`,
          variant: "error"
        });
      }
    } catch (error) {
      const message = await parseError(error);
      toast({
        title: "Error",
        description: message,
        variant: "error"
      });
    }
    setIsSaving(false);
  };
  const handleCancel = () => {
    if (patient) {
      setItemList(getHealthFieldValues(patient, config.type));
      setMedicationEntries(getHealthFieldValueDetails(patient, config.type));
    }
    setInputFields([]);
    setOpen(false);
  };
  const handleKeyPress = (e: React.KeyboardEvent, fieldId_0: string, value_1: string) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addItemFromInput(fieldId_0, value_1);
      // Always add a new input field after pressing enter
      addNewInputField();
    }
  };
  return <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {children || <Button variant="ghost" size="sm" className="flex items-center text-body text-primary hover:bg-primary-50 hover:text-primary-700">
            <FiPlus className="mr-1 h-4 w-4" />
            Add
          </Button>}
      </DialogTrigger>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{config.title}</DialogTitle>
          <DialogDescription>{config.description}</DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-2 py-2">
          <div className="space-y-1">
            {useMedicationSuggestions ? <PatientReportedMedicationInput medications={medicationEntries} onChange={setMedicationEntries} disabled={isSaving} placeholder={config.placeholder} addAnotherPlaceholder={config.addAnotherButtonText} /> : <>
                {itemList.length === 0 && inputFields.length === 0 && <div className="py-4 text-center text-caption text-muted-foreground">
                    No {config.title.toLowerCase()} recorded
                  </div>}
                {itemList.map((item_0, index_2) => <div key={index_2} className="flex min-w-0 items-center gap-2 border-b py-2 last:border-b-0">
                    <div className="min-w-0 flex-1">
                      {editingIndex === index_2 ? <Input value={editingValue} onChange={e_0 => setEditingValue(e_0.target.value)} onBlur={e_1 => {
                  // Don't auto-commit when focus moves to the cancel
                  // button (keyboard tab), so cancel stays reachable
                  if (e_1.relatedTarget?.getAttribute("data-cancel-edit") === "true") {
                    return;
                  }
                  handleSaveEdit(index_2);
                }} onKeyDown={e_2 => {
                  if (e_2.key === "Enter") {
                    e_2.preventDefault();
                    handleSaveEdit(index_2);
                  } else if (e_2.key === "Escape") {
                    handleCancelEdit();
                  }
                }} className="w-full border-0 p-0 text-caption shadow-none focus:ring-0 focus-visible:ring-0" autoFocus /> : <span className="break-words text-caption text-foreground" style={{
                  wordBreak: "break-word",
                  overflowWrap: "break-word"
                }}>
                          {item_0}
                        </span>}
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      {editingIndex === index_2 ? <Button variant="ghost" size="sm" data-cancel-edit="true" onMouseDown={e_3 => e_3.preventDefault()} onClick={handleCancelEdit} icon={FiX} className="h-5 w-5 p-0 hover:bg-hover [&>svg]:h-3 [&>svg]:w-3 [&>svg]:text-muted-foreground" /> : <>
                          <Button variant="ghost" size="sm" onClick={() => handleEditItem(index_2)} icon={FiEdit2} className="h-5 w-5 p-0 hover:bg-hover [&>svg]:h-3 [&>svg]:w-3 [&>svg]:text-muted-foreground" />
                          <Button variant="ghost" size="sm" onClick={() => handleRemoveItem(index_2)} icon={FiTrash2} className="h-5 w-5 p-0 hover:bg-hover [&>svg]:h-3 [&>svg]:w-3 [&>svg]:text-muted-foreground" />
                        </>}
                    </div>
                  </div>)}
                {inputFields.map((field_2, index_3) => <div key={field_2.id} className="flex min-w-0 items-center gap-2 border-b py-2 last:border-b-0">
                    <div className="min-w-0 flex-1">
                      <Input value={field_2.value} onChange={e_4 => updateInputField(field_2.id, e_4.target.value)} onKeyDown={e_5 => handleKeyPress(e_5, field_2.id, field_2.value)} placeholder={config.placeholder} className="w-full border-0 p-0 text-caption shadow-none placeholder:text-foreground-muted focus:ring-0 focus-visible:ring-0" autoFocus={index_3 === inputFields.length - 1} />
                    </div>
                    {inputFields.length > 1 && <Button variant="ghost" size="sm" onClick={() => removeInputField(field_2.id)} icon={FiX} className="h-5 w-5 shrink-0 p-0 hover:bg-hover [&>svg]:h-3 [&>svg]:w-3 [&>svg]:text-muted-foreground" />}
                  </div>)}
                <Button variant="ghost" size="sm" onClick={addNewInputField} icon={FiPlus} className="justify-start p-1 text-caption text-muted-foreground hover:bg-row-hover hover:text-foreground-secondary [&>svg]:mr-1 [&>svg]:h-3 [&>svg]:w-3">
                  {config.addButtonText}
                </Button>
              </>}
          </div>
        </DialogBody>

        <DialogFooter className="gap-2">
          <Button variant="secondary" onClick={handleCancel} isLoading={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} isLoading={isSaving} loadingText="Saving...">
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>;
}